import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Search, Building2, CheckSquare, X, ArrowRight } from 'lucide-react'

export default function GlobalSearch() {
  const [open, setOpen]       = useState(false)
  const [query, setQuery]     = useState('')
  const [results, setResults] = useState({ consultancies: [], tasks: [] })
  const [loading, setLoading] = useState(false)
  const inputRef = useRef(null)
  const navigate = useNavigate()

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(o => !o)
      }
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50)
    else { setQuery(''); setResults({ consultancies: [], tasks: [] }) }
  }, [open])

  useEffect(() => {
    if (!query.trim()) { setResults({ consultancies: [], tasks: [] }); return }
    const timer = setTimeout(() => doSearch(query.trim()), 300)
    return () => clearTimeout(timer)
  }, [query])

  async function doSearch(q) {
    setLoading(true)
    const [{ data: cons }, { data: tks }] = await Promise.all([
      supabase
        .from('consultancies')
        .select('id, name, client_company, status')
        .or(`name.ilike.%${q}%,client_company.ilike.%${q}%`)
        .limit(5),
      supabase
        .from('tasks')
        .select('id, name, status, sections(id, name, phase:phases(id, name, consultancy_id, consultancies(name)))')
        .ilike('name', `%${q}%`)
        .limit(8),
    ])
    setResults({ consultancies: cons ?? [], tasks: tks ?? [] })
    setLoading(false)
  }

  function goTo(path) {
    navigate(path)
    setOpen(false)
  }

  const statusLabel = { active: 'Activa', paused: 'Pausada', completed: 'Completada',
    pending: 'Pendiente', in_progress: 'En progreso', review: 'En revisión', blocked: 'Bloqueado' }
  const statusColor = { active: 'text-green-600', paused: 'text-yellow-600', completed: 'text-brand-600',
    pending: 'text-gray-400', in_progress: 'text-blue-600', review: 'text-purple-600', blocked: 'text-red-500' }

  const total = results.consultancies.length + results.tasks.length
  const hasResults = total > 0

  if (!open) return (
    <button
      onClick={() => setOpen(true)}
      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
      title="Búsqueda global (Ctrl+K)"
    >
      <Search size={15} />
      <span className="hidden sm:inline text-gray-400">Buscar...</span>
      <kbd className="hidden sm:inline ml-1 text-xs bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded font-mono">⌘K</kbd>
    </button>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />

      {/* Panel */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <Search size={18} className="text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="flex-1 text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent"
            placeholder="Buscar consultorías, tareas..."
          />
          {loading && <div className="w-4 h-4 border-2 border-brand-400 border-t-transparent rounded-full animate-spin shrink-0" />}
          <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto">
          {!query && (
            <div className="px-4 py-8 text-center text-sm text-gray-400">
              Escribe para buscar en toda la app
            </div>
          )}

          {query && !loading && !hasResults && (
            <div className="px-4 py-8 text-center text-sm text-gray-400">
              Sin resultados para "{query}"
            </div>
          )}

          {results.consultancies.length > 0 && (
            <div className="p-2">
              <p className="px-2 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Consultorías</p>
              {results.consultancies.map(c => (
                <button
                  key={c.id}
                  onClick={() => goTo(`/consultancy/${c.id}`)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left group"
                >
                  <div className="w-8 h-8 rounded-lg bg-brand-700 flex items-center justify-center text-white text-sm font-bold shrink-0">
                    {c.client_company.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                    <p className="text-xs text-gray-400 truncate">{c.client_company} · <span className={statusColor[c.status]}>{statusLabel[c.status]}</span></p>
                  </div>
                  <ArrowRight size={14} className="text-gray-300 group-hover:text-brand-500 transition-colors shrink-0" />
                </button>
              ))}
            </div>
          )}

          {results.tasks.length > 0 && (
            <div className="p-2 border-t border-gray-50">
              <p className="px-2 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Tareas</p>
              {results.tasks.map(t => {
                const phase = t.sections?.phase
                const consId = phase?.consultancy_id
                return (
                  <button
                    key={t.id}
                    onClick={() => consId && goTo(`/consultancy/${consId}`)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                      <CheckSquare size={15} className="text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{t.name}</p>
                      <p className="text-xs text-gray-400 truncate">
                        {phase?.consultancies?.name ?? 'Consultoría'} · {t.sections?.name} · <span className={statusColor[t.status]}>{statusLabel[t.status]}</span>
                      </p>
                    </div>
                    <ArrowRight size={14} className="text-gray-300 group-hover:text-brand-500 transition-colors shrink-0" />
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-3 text-xs text-gray-400">
          <span><kbd className="bg-gray-100 px-1.5 py-0.5 rounded font-mono">ESC</kbd> cerrar</span>
          <span><kbd className="bg-gray-100 px-1.5 py-0.5 rounded font-mono">↵</kbd> abrir</span>
        </div>
      </div>
    </div>
  )
}
